import { useEffect } from "react";

import { INITIAL_CHAT_SETTINGS } from "~/shared/store/useChatSettingsStore";

import { computeSettingsReconciliation } from "./reconcile-chat-settings";
import type { ChatSettingsOptions } from "./use-chat-settings-options";
import type { ChatSettingsState } from "./use-chat-settings-state";

/**
 * Два эффекта, приводящие выбор пользователя в соответствие с реальным каталогом:
 * восстановление модели, выпавшей из набора artifactType, и подтягивание осей к
 * дефолтам бэкенда при смене model/mode. Держатся вместе, потому что это одна
 * забота — «сохранённый выбор устарел», и порядок между ними значим.
 */
export function useChatSettingsSync(state: ChatSettingsState, options: ChatSettingsOptions): void {
    const { modelId, mode, aspectRatio, resolution, duration } = state;
    const { audioEnabled, characterOrientation, upscaleFactor, batchSize } = state;
    const { setModelId, setMode, setAspectRatio, setResolution, setDuration } = state;
    const { setAudioEnabled, setCharacterOrientation, setUpscaleFactor, setBatchSize } = state;
    const { availableModels, currentModel, availableModes, currentMode } = options;

    // Once models arrive, sync с реальным списком: если выбранная модель не в
    // текущем artifactType-наборе — пытаемся восстановить стабильный дефолт
    // (INITIAL_CHAT_SETTINGS.modelId), иначе fallback на первую доступную
    // (бэк сортирует cheapest-first). Без этой ветки переключение
    // Image → Video → Image «забывает» Pro и оставляет cheapest-image.
    useEffect(() => {
        if (availableModels.length === 0) return;
        if (availableModels.some((m) => m.id === modelId)) return;
        const initialDefault = availableModels.find((m) => m.id === INITIAL_CHAT_SETTINGS.modelId);
        setModelId((initialDefault ?? availableModels[0]!).id);
    }, [availableModels, modelId, setModelId]);

    // Pull-up to backend defaults при смене model/mode. Смена mode инвалидирует
    // mode-зависимые наборы → делаем её первой с early-return (currentMode
    // пересчитается на следующем тике), остальное — через чистый reconcile.
    useEffect(() => {
        if (!currentModel) return;
        if (availableModes.length && !availableModes.includes(mode)) {
            setMode(availableModes[0]!);
            return;
        }
        if (!currentMode) return;
        const next = computeSettingsReconciliation(currentMode, {
            aspectRatio,
            resolution,
            duration,
            audioEnabled,
            characterOrientation,
            upscaleFactor,
            batchSize,
        });
        if (next.aspectRatio !== undefined) setAspectRatio(next.aspectRatio);
        if (next.resolution !== undefined) setResolution(next.resolution);
        if (next.duration !== undefined) setDuration(next.duration);
        if (next.audioEnabled !== undefined) setAudioEnabled(next.audioEnabled);
        if (next.characterOrientation !== undefined) setCharacterOrientation(next.characterOrientation);
        if (next.upscaleFactor !== undefined) setUpscaleFactor(next.upscaleFactor);
        if (next.batchSize !== undefined) setBatchSize(next.batchSize);
    }, [
        currentModel,
        currentMode,
        availableModes,
        mode,
        aspectRatio,
        resolution,
        duration,
        audioEnabled,
        characterOrientation,
        upscaleFactor,
        batchSize,
        setMode,
        setAspectRatio,
        setResolution,
        setDuration,
        setAudioEnabled,
        setCharacterOrientation,
        setUpscaleFactor,
        setBatchSize,
    ]);
}
